import React, { useEffect, useRef, useState } from "react";
import useSWR from "swr";
import { useSearchParams } from "react-router-dom";
import { Loader2, Minus, Sparkles } from "lucide-react";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { DialogTrigger } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { useToast } from "@/components/ui/use-toast";
import { cdnUrl } from "commons/cdn";
import { tagNameBadgeVariant } from "commons/tagName";
import { MyImage } from "components/MyImage";

import { fetcher, formDataFetcher, getFetcher, postFetcher } from "api/swr";
import { TagPickerDialog } from "components/template-tags";
import { Hint } from "./Hint";
import TagPromptLabPromptPanel from "./TagPromptLabPromptPanel";
import { filterBannedTags } from "./promptExclusions";
import { useTagResultTranslation } from "./useTagResultTranslation";

type AnalyzeResult = {
  description: string;
  tags: string[];
  warnings?: string[];
};
type RefImage = {
  key: string;
  imageUrl: string;
  label: string;
  avatarId?: string;
};
type PreparedTemplate = {
  id: string;
  name?: string;
  tags?: string[];
  inDraft?: boolean;
  online?: boolean;
};
type ApplyTagResponse = {
  tags: string[];
  added: string[];
  created: string[];
  skipped: string[];
  errors: string[];
  libraryTags: string[];
  level2Tags?: string[];
};
const tagKey = (tag: string) => tag.trim().toLowerCase();
type PrepareResponse = {
  template: PreparedTemplate;
  preview: { id: string; imageUrl: string } | null;
};
type LabConfig = { providerConfigured: boolean; model: string };
const errorMessage = (error: unknown): string => {
  if (error && typeof error === "object") {
    const detail = error as { message?: string | string[]; error?: string };
    if (Array.isArray(detail.message)) return detail.message.join("；");
    if (detail.message) return detail.message;
    if (detail.error) return detail.error;
  }
  return String(error || "未知错误");
};

export default function TagPromptLabTab() {
  const { toast } = useToast();
  const [searchParams, setSearchParams] = useSearchParams();
  const initialTid = (searchParams.get("tid") || "").trim();
  const [tid, setTid] = useState(initialTid);
  const [template, setTemplate] = useState<PreparedTemplate | null>(null);
  const [images, setImages] = useState<RefImage[]>([]);
  const [prompt, setPrompt] = useState("");
  const [banWords, setBanWords] = useState<string[]>([]);
  const [promptBusy, setPromptBusy] = useState(false);
  const [promptWorking, setPromptWorking] = useState(false);
  const [loadingTemplate, setLoadingTemplate] = useState(false);
  const [analyzing, setAnalyzing] = useState(false);
  const [libraryTags, setLibraryTags] = useState<Set<string>>(new Set());
  const [level2Tags, setLevel2Tags] = useState<Set<string>>(new Set());
  const [applyingTag, setApplyingTag] = useState<string | null>(null);
  const [tagAction, setTagAction] = useState<"add" | "remove">("add");
  const [pickerOpen, setPickerOpen] = useState(false);
  const [applyNotice, setApplyNotice] = useState("");
  const applyLock = useRef(false);
  const appliedTags = new Set((template?.tags || []).map(tagKey));
  const canApply = Boolean(template?.id);
  const [rawResult, setResult] = useState<AnalyzeResult | null>(null);
  const [previousResult, setPreviousResult] = useState<AnalyzeResult | null>(
    null,
  );
  const result = rawResult
    ? { ...rawResult, tags: filterBannedTags(rawResult.tags, banWords) }
    : null;
  const visibleTags = (
    result?.tags.filter((tag) => !appliedTags.has(tagKey(tag))) || []
  ).sort(
    (a, b) =>
      Number(level2Tags.has(tagKey(b))) - Number(level2Tags.has(tagKey(a))) ||
      Number(libraryTags.has(tagKey(b))) - Number(libraryTags.has(tagKey(a))),
  );
  const translation = useTagResultTranslation(rawResult);
  const translations = translation.tags;
  const previousTags = new Set(
    previousResult?.tags.map((tag) => tag.trim().toLowerCase()),
  );
  const currentTags = new Set(
    result?.tags.map((tag) => tag.trim().toLowerCase()),
  );
  const addedTags = previousResult
    ? result?.tags.filter(
        (tag) => !previousTags.has(tag.trim().toLowerCase()),
      ) || []
    : [];
  const removedTags =
    previousResult?.tags.filter(
      (tag) => !currentTags.has(tag.trim().toLowerCase()),
    ) || [];
  const orderChanged = Boolean(
    previousResult &&
    result &&
    previousResult.tags.some(
      (tag, index) =>
        tag.trim().toLowerCase() !== result.tags[index]?.trim().toLowerCase(),
    ),
  );
  const [resultKey, setResultKey] = useState("");
  const [failure, setFailure] = useState("");
  const [generationNotice, setGenerationNotice] = useState("");
  const [suggestedChange, setSuggestedChange] = useState<{
    id: number;
    tag: string;
  }>();
  const generationToken = useRef<number | null>(null);
  const [previewOutcome, setPreviewOutcome] = useState<{
    sequence: number;
    prompt: string;
    success: boolean;
    ban_words: string[];
  }>();
  const requestSeq = useRef(0);
  const controllerRef = useRef<AbortController | null>(null);
  const autoLoadedTid = useRef("");
  const { data: config, error: configError } = useSWR<LabConfig>(
    ["/tag-prompt-lab/config"],
    fetcher,
  );
  const currentKey = JSON.stringify({
    tid: template?.id,
    prompt,
    ban_words: banWords,
    images: images.map((image) => image.imageUrl),
  });
  const stale = Boolean(result && resultKey !== currentKey);
  const templateBusy = loadingTemplate || analyzing || promptWorking || Boolean(applyingTag);
  const busy = templateBusy || promptBusy;
  const generationContext = useRef({
    template,
    images,
    prompt,
    banWords,
    result: rawResult,
    loadingTemplate,
    promptBusy,
    config,
  });
  generationContext.current = {
    template,
    images,
    prompt,
    banWords,
    result: rawResult,
    loadingTemplate,
    promptBusy,
    config,
  };
  const canGenerate = Boolean(
    template && images.length && config?.providerConfigured,
  );
  const suggestExclusion = (tag: string) =>
    setSuggestedChange((previous) => ({
      id: (previous?.id || 0) + 1,
      tag,
    }));

  const cancelGeneration = () => {
    requestSeq.current++;
    controllerRef.current?.abort();
    setAnalyzing(false);
    generationToken.current = null;
  };
  useEffect(
    () => () => {
      controllerRef.current?.abort();
      requestSeq.current++;
    },
    [],
  );

  const handlePrepare = async (overrideTid?: string, syncUrl = true) => {
    const nextTid = (overrideTid ?? tid).trim();
    if (!nextTid) {
      toast({ variant: "destructive", title: "请输入模板 ID" });
      return;
    }
    cancelGeneration();
    const seq = requestSeq.current;
    setLoadingTemplate(true);
    setFailure("");
    setResult(null);
    setLibraryTags(new Set());
    setLevel2Tags(new Set());
    setPickerOpen(false);
    setApplyNotice("");
    setPreviewOutcome(undefined);
    setPreviousResult(null);
    setResultKey("");
    setGenerationNotice("");
    setTemplate(null);
    setImages([]);
    try {
      const prepared = (await getFetcher("/tag-prompt-lab/prepare", {
        tid: nextTid,
      })) as PrepareResponse;
      if (seq !== requestSeq.current) return;
      setTid(nextTid);
      if (syncUrl) {
        autoLoadedTid.current = nextTid;
        setSearchParams(
          (current) => {
            const next = new URLSearchParams(current);
            next.set("tid", nextTid);
            return next;
          },
          { replace: initialTid === nextTid },
        );
      }
      setTemplate(prepared.template);
      setImages(
        prepared.preview?.imageUrl
          ? [
              {
                key: `preview-${prepared.preview.id}`,
                imageUrl: prepared.preview.imageUrl,
                label: "模板预览",
                avatarId: prepared.preview.id,
              },
            ]
          : [],
      );
    } catch (error) {
      if (seq === requestSeq.current)
        setFailure(`获取模板失败：${errorMessage(error)}`);
    } finally {
      if (seq === requestSeq.current) setLoadingTemplate(false);
    }
  };

  useEffect(() => {
    if (!initialTid) {
      autoLoadedTid.current = "";
      cancelGeneration();
      setTid("");
      setLoadingTemplate(false);
      setTemplate(null);
      setImages([]);
      setResult(null);
      setLibraryTags(new Set());
      setLevel2Tags(new Set());
      setPickerOpen(false);
      setApplyNotice("");
      setPreviousResult(null);
      setPreviewOutcome(undefined);
      setResultKey("");
      setGenerationNotice("");
      setFailure("");
      return;
    }
    if (autoLoadedTid.current === initialTid) return;
    autoLoadedTid.current = initialTid;
    setTid(initialTid);
    void handlePrepare(initialTid, false);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [initialTid]);

  const handleAnalyze = async (
    draftPrompt?: string,
    draftBanWords?: string[],
  ): Promise<boolean> => {
    const context = generationContext.current;
    const inputPrompt = draftPrompt ?? context.prompt;
    const inputBanWords = draftBanWords ?? context.banWords;
    if (
      applyLock.current ||
      !context.template ||
      context.loadingTemplate ||
      generationToken.current !== null ||
      (!draftPrompt && context.promptBusy) ||
      !context.images.length ||
      !inputPrompt.trim() ||
      !context.config?.providerConfigured
    )
      return false;
    const body = new FormData();
    body.append("tid", context.template.id);
    body.append("prompt", inputPrompt);
    body.append(
      "imageUrls",
      JSON.stringify(context.images.map((image) => image.imageUrl)),
    );
    body.append("previewOnly", "true");
    body.append("mode", "rebuild");
    const seq = ++requestSeq.current;
    generationToken.current = seq;
    const inputKey = JSON.stringify({
      tid: context.template.id,
      prompt: inputPrompt,
      ban_words: inputBanWords,
      images: context.images.map((image) => image.imageUrl),
    });
    controllerRef.current?.abort();
    const controller = new AbortController();
    controllerRef.current = controller;
    const timer = window.setTimeout(() => controller.abort(), 50_000);
    setAnalyzing(true);
    setFailure("");
    try {
      const response = (await formDataFetcher("/tag-prompt-lab/analyze", {
        arg: body,
        signal: controller.signal,
      })) as {
        result: AnalyzeResult;
        libraryTags?: string[];
        level2Tags?: string[];
      };
      if (seq !== requestSeq.current) return false;
      if (!response?.result || !Array.isArray(response.result.tags))
        throw new Error("没有返回有效的标签结果");
      setPreviousResult(context.result);
      setGenerationNotice("已按当前图片和规则全新生成，点击标签可应用到模板。");
      setResult(response.result);
      setLibraryTags(new Set((response.libraryTags || []).map(tagKey)));
      setLevel2Tags(new Set((response.level2Tags || []).map(tagKey)));
      setResultKey(inputKey);
      setPreviewOutcome({
        sequence: seq,
        prompt: inputPrompt,
        ban_words: inputBanWords,
        success: true,
      });
      return true;
    } catch (error) {
      if (seq === requestSeq.current)
        setPreviewOutcome({
          sequence: seq,
          prompt: inputPrompt,
          ban_words: inputBanWords,
          success: false,
        });
      if (seq === requestSeq.current) {
        const message = controller.signal.aborted
          ? "生成超时，请重试。"
          : `生成失败：${errorMessage(error)}`;
        setFailure(message);
        toast({ variant: "destructive", title: "标签生成失败", description: message });
      }
      return false;
    } finally {
      window.clearTimeout(timer);
      if (generationToken.current === seq) generationToken.current = null;
      if (seq === requestSeq.current) setAnalyzing(false);
    }
  };

  const handleApplyTag = async (
    rawTag: string,
    action: "add" | "remove" = "add",
  ): Promise<boolean> => {
    const tag = rawTag.trim();
    if (!template || !canApply || templateBusy || applyLock.current || !tag)
      return false;
    if (action === "add" && appliedTags.has(tagKey(tag))) {
      setApplyNotice(`模板已有此标签：${tag}`);
      return false;
    }
    const templateId = template.id;
    const seq = requestSeq.current;
    applyLock.current = true;
    setApplyingTag(tag);
    setTagAction(action);
    setApplyNotice("");
    try {
      const response = (await postFetcher(
        action === "remove"
          ? "/tag-prompt-lab/remove-tag"
          : "/tag-prompt-lab/apply-tag",
        {
          arg: { tid: templateId, tag },
        },
      )) as ApplyTagResponse;
      if (seq !== requestSeq.current) return false;
      if (!Array.isArray(response.tags))
        throw new Error("未返回模板当前标签，请重新获取模板确认");
      setTemplate((current) =>
        current?.id === templateId
          ? { ...current, tags: response.tags }
          : current,
      );
      setLibraryTags(
        (current) =>
          new Set([...current, ...(response.libraryTags || []).map(tagKey)]),
      );
      setLevel2Tags(current => new Set([...current, ...(response.level2Tags || []).map(tagKey)]));
      const errors = response.errors || [];
      setApplyNotice(
        errors.length
          ? `打标未完成：${errors.join("；")}`
          : action === "remove"
            ? `已取消关联：${tag}`
            : `已添加标签：${tag}`,
      );
      if (errors.length)
        toast({
          variant: "destructive",
          title: "标签写入未完成",
          description: errors.join("；"),
        });
      return errors.length === 0;
    } catch (error) {
      if (seq !== requestSeq.current) return false;
      setApplyNotice(`打标失败：${errorMessage(error)}`);
      // A disconnected response does not prove the server failed to write.
      try {
        const latest = (await getFetcher("/tag-prompt-lab/prepare", {
          tid: templateId,
        })) as PrepareResponse;
        if (seq === requestSeq.current)
          setTemplate((current) =>
            current?.id === templateId ? latest.template : current,
          );
      } catch {
        /* Keep the error visible; users can reload the template to confirm. */
      }
      return false;
    } finally {
      applyLock.current = false;
      setApplyingTag(null);
    }
  };

  const handlePickerConfirm = async (selected: string[]) => {
    const current = template?.tags || [];
    const selectedKeys = new Set(selected.map(tagKey));
    const currentKeys = new Set(current.map(tagKey));
    const removed = current.filter((tag) => !selectedKeys.has(tagKey(tag)));
    const added = selected.filter((tag) => tag.trim() && !currentKeys.has(tagKey(tag)));
    for (const tag of removed) {
      if (!(await handleApplyTag(tag, "remove"))) {
        throw new Error("标签写入未完成");
      }
    }
    for (const tag of added) {
      if (!(await handleApplyTag(tag, "add"))) {
        throw new Error("标签写入未完成");
      }
    }
  };

  return (
    <div className="flex min-h-0 flex-1 flex-col gap-3 overflow-y-auto xl:overflow-hidden">
      <TagPromptLabPromptPanel
        prompt={prompt}
        onPromptChange={setPrompt}
        onBanWordsChange={setBanWords}
        hasProvider={Boolean(config?.providerConfigured)}
        onBusyChange={setPromptBusy}
        onOperationChange={setPromptWorking}
        generating={analyzing}
        preparing={loadingTemplate || Boolean(applyingTag)}
        canGenerate={canGenerate}
        onTryGenerate={handleAnalyze}
        suggestedChange={suggestedChange}
        previewOutcome={previewOutcome}
        preview={
          <Card
            aria-label="图片与生成结果"
            className="flex h-full min-h-0 min-w-0 flex-col overflow-hidden"
          >
            <div className="shrink-0 space-y-2 border-b px-4 py-3">
              <form
                className="flex min-w-0 flex-wrap items-center gap-2"
                onSubmit={(event) => {
                  event.preventDefault();
                  void handlePrepare();
                }}
              >
                <Label className="shrink-0" htmlFor="tag-prompt-lab-tid">
                  模板 ID
                </Label>
                <Input
                  id="tag-prompt-lab-tid"
                  className="w-56 shrink-0"
                  value={tid}
                  placeholder="输入模板 ID"
                  disabled={templateBusy}
                  onChange={(event) => setTid(event.target.value)}
                />
                <Button
                  type="submit"
                  variant="outline"
                  className="shrink-0"
                  disabled={templateBusy}
                >
                  {loadingTemplate ? (
                    <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  ) : null}
                  获取模板
                </Button>
              </form>
              {configError ? (
                <p role="alert" className="text-sm text-destructive">
                  生成配置加载失败，请刷新重试。
                </p>
              ) : null}
              {config && !config.providerConfigured ? (
                <p role="alert" className="text-sm text-amber-700">
                  当前服务尚未配置 AI 凭证，可以查看模板，暂时无法生成。
                </p>
              ) : null}
              {failure ? (
                <p
                  role="alert"
                  className="whitespace-pre-wrap break-words rounded-lg border border-destructive/30 bg-destructive/5 p-3 text-sm text-destructive"
                >
                  {failure}
                </p>
              ) : null}
            </div>
            <div className="min-h-0 flex-1 space-y-4 overflow-y-auto p-4 xl:flex xl:flex-col xl:overflow-hidden">
              <section className="min-w-0 shrink-0 space-y-3" aria-label="模板信息">
                <h3 className="text-base font-semibold">模板信息</h3>
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>preview</TableHead>
                      <TableHead className="min-w-48">Name</TableHead>
                      <TableHead>Tags</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    <TableRow>
                      <TableCell>
                        {images[0] ? (
                          <MyImage
                            src={cdnUrl(images[0].imageUrl)}
                            alt={template?.name || images[0].label}
                            className="aspect-square h-48 w-48 rounded-md object-contain"
                          />
                        ) : (
                          <span className="text-xs text-muted-foreground">
                            {loadingTemplate ? "正在获取图片…" : "还没有参考图片"}
                          </span>
                        )}
                      </TableCell>
                      <TableCell className="min-w-48">
                        {template ? (
                          <>
                            <div className="break-words font-bold">{template.name}</div>
                            <div className="break-all text-xs text-black/90 dark:text-white/90">
                              {template.id}
                            </div>
                          </>
                        ) : (
                          <span className="text-xs text-muted-foreground">
                            获取模板后显示
                          </span>
                        )}
                      </TableCell>
                      <TableCell>
                        <div aria-label="模板当前标签" className="space-y-2">
                          <div
                            data-current-tags
                            className="flex flex-wrap gap-1"
                          >
                            {(template?.tags || []).map((tag) => (
                              <Badge
                                key={tag}
                                variant={tagNameBadgeVariant(tag)}
                              >
                                <small>
                                  {tag}
                                  {tagAction === "remove" && applyingTag === tag ? (
                                    <Loader2 className="ml-1 inline h-3 w-3 animate-spin" />
                                  ) : null}
                                </small>
                              </Badge>
                            ))}
                            {!template?.tags?.length ? (
                              <span className="text-xs text-muted-foreground">
                                {template ? "暂无标签" : "获取模板后显示"}
                              </span>
                            ) : null}
                          </div>
                          <TagPickerDialog
                            open={pickerOpen}
                            onOpenChange={setPickerOpen}
                            title="选择标签"
                            initialTags={template?.tags || []}
                            confirmLabel="保存标签"
                            busy={Boolean(applyingTag)}
                            onConfirm={handlePickerConfirm}
                            trigger={
                              <DialogTrigger asChild>
                                <Button
                                  type="button"
                                  variant="outline"
                                  size="sm"
                                  aria-label="选择标签"
                                  disabled={templateBusy || !canApply}
                                >
                                  选择标签
                                </Button>
                              </DialogTrigger>
                            }
                          />
                          {applyingTag ? (
                            <p role="status" className="text-xs text-muted-foreground">
                              {tagAction === "remove" ? "正在取消关联" : "正在添加"}{" "}
                              {applyingTag}…
                            </p>
                          ) : null}
                          {applyNotice ? (
                            <p role="status" className="text-xs text-muted-foreground">
                              {applyNotice}
                            </p>
                          ) : null}
                        </div>
                      </TableCell>
                    </TableRow>
                  </TableBody>
                </Table>
              </section>
              <section
                aria-label="AI 标签"
                className="min-w-0 overflow-hidden rounded-lg border border-primary/20 xl:flex xl:min-h-0 xl:flex-1 xl:flex-col"
              >
                <div className="border-b bg-muted/20 px-4 py-3">
                  <div className="flex flex-wrap items-center justify-between gap-3">
                    <div className="flex flex-wrap items-center gap-x-3 gap-y-1">
                      <h3 className="text-base font-semibold">
                        <span className="font-bold">tags</span>
                        {result ? (
                          <Badge variant="secondary" className="ml-2">
                            {visibleTags.length}
                          </Badge>
                        ) : null}
                      </h3>
                      {result && previousResult ? (
                        <p className="text-xs text-muted-foreground">
                          蓝色加粗为相比上次生成新增的标签
                        </p>
                      ) : null}
                    </div>
                  <div className="flex flex-wrap gap-2">
                    <Button
                      type="button"
                      disabled={
                        busy ||
                        !template ||
                        !images.length ||
                        !config?.providerConfigured ||
                        !prompt.trim()
                      }
                      onClick={() => void handleAnalyze()}
                    >
                      {analyzing ? (
                        <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                      ) : (
                        <Sparkles className="mr-2 h-4 w-4" />
                      )}
                      {analyzing ? "正在生成" : "生成标签"}
                    </Button>
                  </div>
                </div>
              </div>
              <div className="xl:flex xl:min-h-0 xl:flex-1 xl:flex-col">
              <div
                className="space-y-4 p-4 xl:min-h-0 xl:flex-1 xl:overflow-y-auto"
                aria-live="polite"
                aria-busy={analyzing}
              >
                {result ? (
                  <>
                    {stale ? (
                      <p className="rounded-lg bg-amber-50 p-3 text-sm text-amber-800 dark:bg-amber-950/30 dark:text-amber-200">
                        待重新生成：草稿已修改，下方是上次结果。点击「生成标签」查看新效果。
                      </p>
                    ) : null}
                    {analyzing ? (
                      <p className="text-sm text-muted-foreground">
                        正在生成新结果，下方暂时保留上次预览。
                      </p>
                    ) : null}
                    <section>
                      <div className="flex flex-wrap gap-2.5">
                        {visibleTags.length ? (
                          visibleTags.map((tag, index) => (
                            <div
                              key={tag}
                              data-tag-level={level2Tags.has(tagKey(tag)) ? "2" : libraryTags.has(tagKey(tag)) ? "3" : undefined}
                              data-library-tag={
                                libraryTags.has(tagKey(tag)) ? "true" : "false"
                              }
                              className={`inline-flex max-w-full cursor-pointer items-stretch rounded-lg border-2 text-sm ${
                                level2Tags.has(tagKey(tag))
                                  ? "border-red-500 bg-red-50 text-red-950 dark:border-red-400 dark:bg-red-950/40 dark:text-red-100"
                                  : libraryTags.has(tagKey(tag))
                                  ? "border-amber-500 bg-amber-50 text-amber-950 dark:border-amber-400 dark:bg-amber-950/40 dark:text-amber-100"
                                  : "border-primary/25 bg-primary/5 text-slate-900 dark:text-slate-100"
                              }`}
                            >
                              <Hint
                                className="min-w-0"
                                content={
                                  previousResult && !previousTags.has(tagKey(tag))
                                    ? "相比上次生成新增"
                                    : level2Tags.has(tagKey(tag))
                                    ? "二级标签，点击应用到模板"
                                    : libraryTags.has(tagKey(tag))
                                    ? "三级词库已有，点击应用到模板"
                                    : "点击创建标签并应用到模板"
                                }
                              >
                              <button
                                type="button"
                                disabled={templateBusy || !canApply}
                                onClick={() => void handleApplyTag(tag)}
                                aria-label={`添加标签 ${tag}`}
                                className="cursor-pointer disabled:cursor-pointer inline-flex min-w-0 items-center gap-2 rounded-l-md px-2 py-1 text-left text-inherit enabled:hover:bg-black/5 dark:enabled:hover:bg-white/5 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring disabled:opacity-100"
                              >
                                <span className="text-[10px] tabular-nums text-muted-foreground">
                                  {String(index + 1).padStart(2, "0")}
                                </span>
                                {previousResult &&
                                !previousTags.has(tagKey(tag)) ? (
                                  <strong className="break-words font-bold text-blue-600 dark:text-blue-400">
                                    {tag}
                                  </strong>
                                ) : (
                                  <span className="break-words">{tag}</span>
                                )}
                                {translations.get(tag) ? (
                                  <span
                                    lang="zh-CN"
                                    className="break-words text-slate-700 dark:text-slate-200"
                                  >
                                    · {translations.get(tag)}
                                  </span>
                                ) : null}
                                {applyingTag === tag ? (
                                  <Loader2 className="h-3 w-3 shrink-0 animate-spin" />
                                ) : null}
                              </button>
                              </Hint>
                              <Hint content="加入禁止词列表">
                              <button
                                type="button"
                                disabled={busy}
                                onClick={() => suggestExclusion(tag)}
                                aria-label={`不要生成标签 ${tag}`}
                                className="cursor-pointer disabled:cursor-pointer shrink-0 rounded-r-md border-l border-primary/20 px-1.5 hover:bg-destructive/10 hover:text-destructive focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring disabled:opacity-50"
                              >
                                <Minus
                                  aria-hidden="true"
                                  className="h-3.5 w-3.5"
                                />
                              </button>
                              </Hint>
                            </div>
                          ))
                        ) : (
                          <p className="text-sm text-muted-foreground">
                            {result.tags.length
                              ? "生成的标签已全部关联到模板。"
                              : "没有生成标签，可以编辑提示词后再试。"}
                          </p>
                        )}
                      </div>
                      {visibleTags.length ? (
                        <p className="mt-2 text-xs text-muted-foreground">
                          红色表示二级标签，黄色表示词库已有的三级标签；已有标签优先展示。点击标签应用到模板，右侧减号加入禁止词列表。
                        </p>
                      ) : null}
                    </section>
                    {previousResult ? (
                      <div
                        aria-label="本次标签变化"
                        className="space-y-1 rounded-lg bg-muted/30 px-3 py-2 text-xs"
                      >
                        <p className="text-muted-foreground">
                          相比上次成功生成
                        </p>
                        {addedTags.length || removedTags.length ? (
                          <>
                            <p>
                              新增：
                              {addedTags.length ? addedTags.join("、") : "无"}
                            </p>
                            <p>
                              移除：
                              {removedTags.length
                                ? removedTags.join("、")
                                : "无"}
                            </p>
                          </>
                        ) : (
                          <p>
                            {orderChanged
                              ? "标签未增减，排序已调整。"
                              : "标签及排序没有变化。"}
                          </p>
                        )}
                      </div>
                    ) : null}
                  </>
                ) : (
                  <div className="flex min-h-36 flex-col items-center justify-center gap-3 text-center text-muted-foreground xl:h-full">
                    <div className="rounded-2xl bg-primary/5 p-4">
                      {analyzing ? (
                        <Loader2 className="h-7 w-7 animate-spin text-primary" />
                      ) : (
                        <Sparkles className="h-7 w-7 text-primary" />
                      )}
                    </div>
                    <p className="font-medium text-foreground">
                      {analyzing ? "正在理解图片" : "描述和标签将在这里显示"}
                    </p>
                    <p className="max-w-xs text-sm">
                      {analyzing
                        ? "完成后会展示完整生成结果。"
                        : "获取模板后点击生成，也可以先在右侧修改提示词。"}
                    </p>
                  </div>
                )}
              </div>
              {result ? (
                <div className="space-y-4 border-t p-4 xl:shrink-0">
                  <details className="rounded-lg border bg-muted/20 px-3 py-2">
                    <summary className="cursor-pointer text-sm font-medium focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring">
                      图片描述
                    </summary>
                    <p className="mt-2 whitespace-pre-wrap break-words text-sm leading-6 text-muted-foreground">
                      {result.description ||
                        "本次结果暂无图片描述，可再次生成。"}
                    </p>
                    {translation.description ? (
                      <p
                        lang="zh-CN"
                        className="mt-2 whitespace-pre-wrap break-words text-sm leading-6 text-muted-foreground"
                      >
                        {translation.description}
                      </p>
                    ) : null}
                  </details>
                  <div className="space-y-1 text-[11px] leading-relaxed text-muted-foreground/80">
                    {translation.status ? (
                      <p role="status">{translation.status}</p>
                    ) : null}
                    {translation.error ? (
                      <p>
                        {translation.error}{" "}
                        <button
                          type="button"
                          className="underline"
                          onClick={translation.retry}
                        >
                          重试翻译
                        </button>
                      </p>
                    ) : null}
                    {generationNotice ? (
                      <p role="status">{generationNotice}</p>
                    ) : null}
                    <p>
                      目标约 15
                      个有依据的标签，不强行凑数。修改规则不代表指定词一定生成；还需符合图片内容和禁止规则。
                    </p>
                  </div>
                </div>
              ) : null}
              </div>
              </section>
            </div>
          </Card>
        }
      />
    </div>
  );
}
